import type { Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import { ANNOTATION_TYPE } from '../../../../labelling/constants';
import { getSubtractedPolygonDataPoints, transformRectanglesToPolygons } from '../../../../labelling/reducers/actionReducers/multi-select/helpers';
import type { AnnotationT, PolygonAnnotationT } from '../../../../labelling/types';
import { buildAnnotationForOutput } from '../../../utils/build-annotation-for-output';
import { annotationLabelsAtom, annotationMetadataAtom, loadedAnnotationFramesAtom } from '../../annotation-atoms';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import { selectedAnnotationIdsAtom } from '../../tool-atoms';
import { currentFrameAtom } from '../../video-playback-atoms';
import type { SubtractPolygonEventPayload } from '../events';
export const subtractPolygonCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<SubtractPolygonEventPayload, void> => {
  let firstAnnotationId = '';
  let previousAnnotation: AnnotationT | undefined;
  let previousSelectedIds: string[] = [];
  let selectedFrameIndex = -1;
  return {
    execute: () => {
      const currentFrame = get(currentFrameAtom);
      const selectedIds = get(selectedAnnotationIdsAtom);
      const frameMap = get(loadedAnnotationFramesAtom).get(currentFrame);
      if (!frameMap || selectedIds.length < 2) {
        return;
      }
      if (!selectedIds[0]) {
        return;
      }
      firstAnnotationId = selectedIds[0];
      const metadata = get(annotationMetadataAtom);
      const annotationLabelsList = get(annotationLabelsAtom);
      const firstFramePayload = frameMap.get(firstAnnotationId);
      const firstMeta = metadata.get(firstAnnotationId);
      if (!firstFramePayload || !firstMeta) {
        return;
      }
      previousAnnotation = buildAnnotationForOutput(firstFramePayload, firstMeta, annotationLabelsList, currentFrame);
      previousSelectedIds = selectedIds;
      selectedFrameIndex = currentFrame;
      const annotations = selectedIds.map(id => {
        const framePayload = frameMap.get(id);
        const meta = metadata.get(id);
        if (!framePayload || !meta) {
          return undefined;
        }
        return buildAnnotationForOutput(framePayload, meta, annotationLabelsList, currentFrame);
      }).filter((a): a is AnnotationT => !!a);
      const annotationsCopy: AnnotationT[] = annotations.map(a => ({
        ...a
      }));
      transformRectanglesToPolygons(annotationsCopy);
      const subtractedPoints = getSubtractedPolygonDataPoints((annotationsCopy as PolygonAnnotationT[]));
      const updatedAnnotation: PolygonAnnotationT = {
        ...previousAnnotation,
        annotationType: ANNOTATION_TYPE.POLYGON,
        annotationLabel: previousAnnotation.annotationLabel,
        data: {
          points: [...subtractedPoints]
        }
      };
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex: selectedFrameIndex,
        annotationId: firstAnnotationId,
        annotation: updatedAnnotation
      });
      set(selectedAnnotationIdsAtom, [firstAnnotationId]);
    },
    undo: () => {
      if (!previousAnnotation || !firstAnnotationId) {
        return;
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex: selectedFrameIndex,
        annotationId: firstAnnotationId,
        annotation: previousAnnotation
      });
      set(selectedAnnotationIdsAtom, previousSelectedIds);
    }
  };
};